import { useCallback } from 'react';
import { type Href, useRouter } from 'expo-router';
import { defineMessages, type MessageValue, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@sg/garnish';

import { useCart } from '../../../useCart/useCart';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Takes group order participants to the group order's menu when they view the
 * menu of another location, since only the host can change the location.
 */
export const useFollowGroupOrderLocation = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const { cart, cartOrderType } = useCart();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isGroupOrderParticipant = Boolean(
    cart?.multipartyDetails?.joinCode && !cart.currentUserOrderPermissions.canSubmitOrder,
  );

  const isDelivery = cartOrderType === 'delivery';
  const restaurantSlug = cart?.restaurant?.slug;
  const restaurantName = cart?.restaurant?.name;
  const deliveryAddressId = cart?.deliveryOrderDetail?.address?.id;
  const deliveryStreet = cart?.deliveryOrderDetail?.address?.street;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const followGroupOrderLocation = useCallback(() => {
    const menuHref = getGroupOrderMenuHref({ isDelivery, deliveryAddressId, restaurantSlug });
    const locationName = isDelivery ? deliveryStreet : restaurantName;

    if (!menuHref) {
      return;
    }

    router.replace(menuHref);

    if (!locationName) {
      return;
    }

    // Leaving the bag clears notice banners right after this runs, so the banner waits a tick.
    setTimeout(() => {
      addNoticeBanner(
        {
          id: GROUP_ORDER_LOCATION_BANNER_ID,
          text: formatMessage(messages.locationChanged, { location_name: locationName }),
          palette: 'neutral',
        },
        true,
      );
    });
  }, [
    addNoticeBanner,
    deliveryAddressId,
    deliveryStreet,
    formatMessage,
    isDelivery,
    restaurantName,
    restaurantSlug,
    router,
  ]);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isGroupOrderParticipant,
    followGroupOrderLocation,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getGroupOrderMenuHref(params: GetGroupOrderMenuHrefParams) {
  const { isDelivery, deliveryAddressId, restaurantSlug } = params;

  if (isDelivery && deliveryAddressId) {
    return {
      pathname: '/delivery/[addressId]/menu',
      params: { addressId: deliveryAddressId },
    } satisfies Href;
  }

  if (isDelivery || !restaurantSlug) {
    return;
  }

  return { pathname: '/[restaurantSlug]/menu', params: { restaurantSlug } } satisfies Href;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly locationChanged: { readonly location_name: MessageValue };
}>({
  locationChanged: {
    defaultMessage: 'The host moved this group order to {location_name}.',
    description: 'Reorder | Group order location change banner',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_ORDER_LOCATION_BANNER_ID = 'group-order-location-changed';

// ─── Types ───────────────────────────────────────────────────────────────────

type GetGroupOrderMenuHrefParams = {
  isDelivery: boolean;
  deliveryAddressId: string | undefined;
  restaurantSlug: string | undefined;
};
